interface TableValueViewProps {
  /** Rows extracted by the AI for this Table-typed field. */
  rows: ReadonlyArray<Record<string, unknown>>;
}

/**
 * Read-only render of the value of a Table-typed extracted field.
 *
 * Columns are inferred from the union of keys across all rows so the view
 * tolerates rows that omit optional columns. Cell editing is intentionally
 * deferred to Wave 6.B (PATCH /fields/{name} extension to address table
 * cells via "{field}[{i}].{column}" path).
 *
 * The reviewer's schema column metadata is not currently exposed in
 * DocumentResultResponse — inferring from data keys is the pragmatic v1
 * approach. A future commit can enrich the result with the schema slice so
 * column types render typed (date vs string vs decimal alignment).
 */
export function TableValueView({ rows }: TableValueViewProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
        Sin filas extraídas para este campo.
      </div>
    );
  }

  const columns = collectColumns(rows);

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <table className="w-full text-left text-xs">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-2 py-1.5 text-right text-slate-400">#</th>
            {columns.map((col) => (
              <th key={col} className="px-2 py-1.5">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-slate-50">
              <td className="px-2 py-1.5 text-right text-slate-400 tabular-nums">{rowIndex + 1}</td>
              {columns.map((col) => (
                <td key={col} className="px-2 py-1.5 align-top">
                  {formatCell(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function collectColumns(rows: ReadonlyArray<Record<string, unknown>>): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        order.push(key);
      }
    }
  }
  return order;
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  // Fall back to JSON for nested structures — shouldn't happen given the schema
  // rejects nested tables, but defensive rendering avoids React's "objects are
  // not valid as a React child" error.
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}
